import { useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import * as Yup from 'yup';

import Button from '@/components/Button';
import {
	CfCaptchaWidget,
	useCfTurnstileChallenge
} from '@/components/CfCaptchaWidget';
import ErrorMessage from '@/components/form/ErrorMessage';
import Input from '@/components/form/Input';
import { useGetErrorMessage } from '@/hooks';
import { useLazyFetch } from '@/hooks/fetch';
import Circle from '@/icons/circle.svg';
import authModel from '@/models/auth';
import { AUTH_MODAL_TYPES, openAuthModal } from '@/store/profile';

import Description from './components/Description';
import LevelPassword from './components/LevelPassword';
import TextLink from './components/TextLink';
import Title from './components/Title';

function RequirementItem({ text }) {
	return (
		<div className="flex items-start">
			<div className="mr-2 mt-[5px] flex-shrink-0 md:mt-1.5">
				<Circle className="h-1 w-1 md:h-2 md:w-2" />
			</div>
			<p className="text-[10px] leading-4 text-grey-500 md:text-xs md:leading-5">
				{text}
			</p>
		</div>
	);
}

function ChangePassword() {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const location = useLocation();
	const getErrorMessage = useGetErrorMessage();
	const {
		cfChallengeToken,
		cfChallengeCompleted,
		cfIdempotencyKey,
		triggerChallenge
	} = useCfTurnstileChallenge({
		action: 'changePassword'
	});

	const schema = Yup.object({
		password: Yup.string().required(),
		confirmPassword: Yup.string()
			.required()
			.oneOf(
				[Yup.ref('password'), null],
				t('authModal.changePassword.passwordsMustMatch')
			)
	});
	const methods = useForm({
		defaultValues: { confirmPassword: '', password: '' },
		mode: 'onChange',
		resolver: yupResolver(schema)
	});

	const {
		fetch: updatePassword,
		isSuccess,
		isLoading,
		error
	} = useLazyFetch(authModel.updatePassword.bind(authModel));
	const errorMessage = getErrorMessage(error);

	const requirements = t('authModal.changePassword.requirements', {
		returnObjects: true
	});

	const invalidToken = error?.data?.errorCode === 'invalid_token';

	const { password } = methods.watch();

	useEffect(() => {
		if (password) {
			triggerChallenge();
		}
	}, [password]);

	function onSubmit() {
		return updatePassword({
			password,
			token: location.state?.resetPasswordToken,
			cfChallengeToken,
			cfIdempotencyKey
		});
	}

	return (
		<>
			{error?.data?.errorCode === 'invalid_token' && (
				<>
					<Title>{t('authModal.changePassword.expiredToken.title')}</Title>
					<Description>
						{t('authModal.changePassword.expiredToken.description')}
					</Description>
					<Button
						type="button"
						className="mt-4 w-full md:mt-5"
						onClick={() =>
							dispatch(
								openAuthModal({
									type: AUTH_MODAL_TYPES.RECOVER_PASSWORD
								})
							)
						}
					>
						{t('common.accept')}
					</Button>
				</>
			)}
			{isSuccess && (
				<>
					<Title>{t('authModal.changePassword.success.title')}</Title>
					<Description>
						{t('authModal.changePassword.success.description')}
					</Description>
					<Button
						type="button"
						className="mt-4 w-full md:mt-5"
						disabled={!cfChallengeCompleted}
						onClick={() =>
							dispatch(
								openAuthModal({
									type: AUTH_MODAL_TYPES.LOGIN
								})
							)
						}
					>
						{t('common.accept')}
					</Button>
				</>
			)}
			{!invalidToken && !isSuccess && (
				<>
					<Title>{t('authModal.changePassword.title')}</Title>
					<Description>
						{t('authModal.changePassword.description')}
					</Description>
					<div className="my-4 flex flex-col gap-2 md:mb-6 md:mt-3">
						{requirements.map((requirement, index) => (
							<RequirementItem key={index} text={requirement} />
						))}
					</div>

					<FormProvider {...methods}>
						<form onSubmit={methods.handleSubmit(onSubmit)}>
							<div className="mb-3 flex flex-col gap-2 md:mb-5 md:gap-4">
								<Input
									name="password"
									type="password"
									placeholder={t(
										'authModal.changePassword.newPassword'
									)}
								/>
								<Input
									name="confirmPassword"
									type="password"
									placeholder={t(
										'authModal.changePassword.confirmNewPassword'
									)}
								/>
								<LevelPassword password={methods.watch('password')} />

								<CfCaptchaWidget />
							</div>
							<Button
								type="submit"
								className="w-full"
								loading={isLoading}
								disabled={!methods.formState.isValid}
							>
								{t('authModal.changePassword.confirmPassword')}
							</Button>
							{errorMessage && (
								<ErrorMessage>{errorMessage}</ErrorMessage>
							)}
						</form>

						<TextLink
							text={t('authModal.didYouRemember')}
							linkText={t('authModal.signIn')}
							disabled={isLoading}
							onClick={() =>
								dispatch(
									openAuthModal({
										type: AUTH_MODAL_TYPES.LOGIN
									})
								)
							}
						/>
					</FormProvider>
				</>
			)}
		</>
	);
}

export default ChangePassword;
